import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

const DAYS   = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return `${DAYS[date.getDay()]} ${d} ${MONTHS[m - 1]} ${y}`;
}

function IconPencil() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}

function IconBook() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  );
}

function JournalPage({ appTheme, isMobile, userId }) {
  const [entries,       setEntries]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [view,          setView]          = useState("list");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [sort,          setSort]          = useState("newest");
  const [isEditing,     setIsEditing]     = useState(false);
  const [editDraft,     setEditDraft]     = useState("");
  const [showDeleteSheet, setShowDeleteSheet] = useState(false);
  const [entryMeals,    setEntryMeals]    = useState([]);
  const [mealsLoading,  setMealsLoading]  = useState(false);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    supabase
      .from("daily_notes")
      .select("id, date, note, source")
      .eq("user_id", userId)
      .order("date", { ascending: false })
      .then(({ data, error }) => {
        setLoading(false);
        if (error) { console.error("JournalPage fetch:", error); return; }
        setEntries(data || []);
      });
  }, [userId]);

  function openEntry(entry) {
    setSelectedEntry(entry);
    setEditDraft(entry.note);
    setIsEditing(false);
    setEntryMeals([]);
    setView("entry");
    if (!userId) return;
    setMealsLoading(true);
    supabase
      .from("meal_plan_entries")
      .select("meal_type, meal_name, snack_data")
      .eq("user_id", userId)
      .eq("date", entry.date)
      .then(({ data, error }) => {
        setMealsLoading(false);
        if (!error) setEntryMeals(data || []);
      });
  }

  function handleSave() {
    const trimmed = editDraft.trim();
    if (!trimmed || !selectedEntry) return;
    const updated = { ...selectedEntry, note: trimmed };
    setSelectedEntry(updated);
    setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
    setIsEditing(false);
    supabase
      .from("daily_notes")
      .upsert({ user_id: userId, date: updated.date, note: trimmed }, { onConflict: "user_id,date" })
      .then(({ error }) => { if (error) console.error("JournalPage save:", error); });
  }

  function handleDelete() {
    if (!selectedEntry) return;
    const deletedId = selectedEntry.id;
    supabase
      .from("daily_notes")
      .delete()
      .eq("id", deletedId)
      .then(({ error }) => { if (error) console.error("JournalPage delete:", error); });
    setEntries(prev => prev.filter(e => e.id !== deletedId));
    setShowDeleteSheet(false);
    setView("list");
    setSelectedEntry(null);
  }

  const sorted = sort === "newest"
    ? [...entries].sort((a, b) => b.date.localeCompare(a.date))
    : [...entries].sort((a, b) => a.date.localeCompare(b.date));

  const dividerColor = appTheme.cardBorder.replace("1px solid ", "");

  const iconBtnStyle = {
    width: "36px",
    height: "36px",
    borderRadius: "999px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: appTheme.primaryText,
    padding: 0,
    flexShrink: 0,
  };

  // ── MEAL SECTION ─────────────────────────────────────────────────────────────

  function renderMeals() {
    if (mealsLoading) {
      return <div style={{ fontSize: "13px", color: appTheme.mutedText }}>Loading…</div>;
    }
    const breakfast = entryMeals.find(m => m.meal_type === "breakfast");
    const lunch     = entryMeals.find(m => m.meal_type === "lunch");
    const dinner    = entryMeals.find(m => m.meal_type === "dinner");
    const snackRow  = entryMeals.find(m => m.meal_type === "snacks");
    const snacks    = snackRow && Array.isArray(snackRow.snack_data) ? snackRow.snack_data : [];

    const hasMeals = breakfast || lunch || dinner || snacks.length > 0;
    if (!hasMeals) {
      return (
        <div style={{ fontSize: "13px", color: appTheme.mutedText, fontStyle: "italic", fontFamily: appTheme.fontBody }}>
          No meals planned this day
        </div>
      );
    }

    const rows = [];
    if (breakfast)       rows.push({ label: "Breakfast", name: breakfast.meal_name });
    if (lunch)           rows.push({ label: "Lunch",     name: lunch.meal_name });
    if (dinner)          rows.push({ label: "Dinner",    name: dinner.meal_name });
    if (snacks.length > 0) {
      const names = snacks.map(s => typeof s === "string" ? s : (s?.name || "Snack")).join(", ");
      rows.push({ label: "Snacks", name: names });
    }

    return rows.map(({ label, name }) => (
      <div key={label} style={{ display: "flex", gap: "10px", marginBottom: "8px", alignItems: "flex-start" }}>
        <span style={{
          fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em",
          textTransform: "uppercase", color: appTheme.mutedText,
          fontFamily: appTheme.fontBody, minWidth: "68px", paddingTop: "2px"
        }}>
          {label}
        </span>
        <span style={{ fontSize: "13px", color: appTheme.secondaryText, fontFamily: appTheme.fontBody, lineHeight: 1.45 }}>
          {name || "—"}
        </span>
      </div>
    ));
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────

  const listView = (
    <div style={{ display: "grid", gap: "10px" }}>

      {/* Sort toggle */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "2px" }}>
        <div style={{
          display: "flex",
          background: appTheme.softBackground || "#F0E9DC",
          borderRadius: "10px",
          padding: "3px",
          gap: "2px"
        }}>
          {["newest", "oldest"].map(opt => (
            <button
              key={opt}
              onClick={() => setSort(opt)}
              style={{
                padding: "5px 12px",
                borderRadius: "7px",
                border: "none",
                background: sort === opt ? appTheme.cardBackground : "transparent",
                color: sort === opt ? appTheme.primaryText : appTheme.mutedText,
                fontFamily: appTheme.fontBody,
                fontSize: "12px",
                fontWeight: sort === opt ? "700" : "500",
                cursor: "pointer",
                boxShadow: sort === opt ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s ease",
              }}
            >
              {opt === "newest" ? "Newest" : "Oldest"}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: "center", padding: "40px 0", color: appTheme.mutedText, fontFamily: appTheme.fontBody, fontSize: "14px" }}>
          Loading…
        </div>
      )}

      {/* Empty state */}
      {!loading && sorted.length === 0 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "52px 24px", gap: "14px", textAlign: "center" }}>
          <div style={{ color: "rgba(180, 160, 210, 0.8)" }}>
            <IconBook />
          </div>
          <div style={{ fontSize: "17px", fontWeight: "600", color: appTheme.primaryText, fontFamily: appTheme.fontHeading }}>
            No journal entries yet
          </div>
          <div style={{ fontSize: "14px", color: appTheme.secondaryText, fontFamily: appTheme.fontBody, lineHeight: 1.5, maxWidth: "220px" }}>
            Add your first note from any day in the Plan tab
          </div>
        </div>
      )}

      {/* Entry cards */}
      {!loading && sorted.map(entry => (
        <button
          key={entry.id}
          onClick={() => openEntry(entry)}
          style={{
            width: "100%",
            background: appTheme.cardBackground,
            border: appTheme.cardBorder,
            borderRadius: "20px",
            boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
            padding: 0,
            cursor: "pointer",
            textAlign: "left",
            overflow: "hidden",
          }}
        >
          {/* Date row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 12px", gap: "8px" }}>
            <span style={{
              fontSize: "14px",
              fontFamily: appTheme.fontHeading,
              fontWeight: "400",
              color: appTheme.primaryText,
              lineHeight: 1.2,
              flex: 1,
            }}>
              {formatDate(entry.date)}
            </span>
            <span style={{
              fontSize: "10px",
              fontWeight: "600",
              fontFamily: appTheme.fontBody,
              padding: "3px 9px",
              borderRadius: "999px",
              flexShrink: 0,
              ...(entry.source === "programme"
                ? { background: appTheme.accentColor, color: "#ffffff" }
                : { background: appTheme.softBackground, color: appTheme.secondaryText }
              ),
            }}>
              {entry.source === "programme" ? "Programme" : "Manual"}
            </span>
            <span style={{ fontSize: "20px", color: appTheme.mutedText, lineHeight: 1, flexShrink: 0 }}>›</span>
          </div>

          {/* Divider */}
          <div style={{ height: "1px", background: dividerColor, margin: "0 16px" }} />

          {/* Note preview */}
          <div style={{ padding: "12px 16px 6px" }}>
            <div style={{
              fontSize: "13px",
              fontFamily: appTheme.fontBody,
              color: appTheme.secondaryText,
              lineHeight: 1.55,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}>
              {entry.note}
            </div>
          </div>

          {/* Character count */}
          <div style={{ padding: "4px 16px 14px" }}>
            <span style={{ fontSize: "11px", color: appTheme.mutedText, fontFamily: appTheme.fontBody }}>
              {entry.note.length} characters
            </span>
          </div>
        </button>
      ))}
    </div>
  );

  // ── ENTRY VIEW (fixed overlay) ────────────────────────────────────────────────

  const entryView = view === "entry" && selectedEntry && (
    <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 2000,
      background: appTheme.pageBackground,
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
    }}>
      <div style={{ padding: isMobile ? "18px 16px 40px" : "24px 24px 48px", maxWidth: "680px", margin: "0 auto" }}>

        {/* Top bar */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "40px 1fr auto",
          alignItems: "center",
          marginBottom: "24px",
          gap: "8px",
        }}>
          <button
            onClick={() => { setView("list"); setIsEditing(false); setShowDeleteSheet(false); }}
            style={{ ...iconBtnStyle, fontSize: "24px" }}
          >
            ‹
          </button>
          <div />
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {isEditing ? (
              <button
                onClick={handleSave}
                style={{
                  padding: "7px 16px",
                  borderRadius: "10px",
                  border: "none",
                  background: appTheme.accentColor,
                  color: "#fff",
                  fontFamily: appTheme.fontBody,
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                Save
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                style={{ ...iconBtnStyle, color: appTheme.secondaryText }}
                aria-label="Edit note"
              >
                <IconPencil />
              </button>
            )}
            <button
              onClick={() => setShowDeleteSheet(true)}
              style={{ ...iconBtnStyle, color: appTheme.actionColor || "#E6544C" }}
              aria-label="Delete entry"
            >
              <IconTrash />
            </button>
          </div>
        </div>

        {/* Date heading */}
        <div style={{
          fontFamily: appTheme.fontHeading,
          fontSize: isMobile ? "26px" : "30px",
          fontWeight: "600",
          color: appTheme.primaryText,
          lineHeight: 1.15,
          marginBottom: "20px",
        }}>
          {formatDate(selectedEntry.date)}
        </div>

        {/* Note text / edit area */}
        {isEditing ? (
          <textarea
            value={editDraft}
            onChange={e => setEditDraft(e.target.value)}
            autoFocus
            style={{
              width: "100%",
              boxSizing: "border-box",
              minHeight: "180px",
              padding: "14px 16px",
              borderRadius: "16px",
              border: appTheme.accentBorder,
              background: appTheme.inputBackground || appTheme.cardBackground,
              fontFamily: appTheme.fontBody,
              fontSize: "15px",
              color: appTheme.primaryText,
              lineHeight: 1.65,
              outline: "none",
              resize: "vertical",
              marginBottom: "28px",
            }}
          />
        ) : (
          <div style={{
            fontFamily: appTheme.fontBody,
            fontSize: "15px",
            color: appTheme.primaryText,
            lineHeight: 1.7,
            whiteSpace: "pre-wrap",
            marginBottom: "28px",
          }}>
            {selectedEntry.note}
          </div>
        )}

        {/* Divider */}
        <div style={{ height: "1px", background: dividerColor, marginBottom: "20px" }} />

        {/* What you ate */}
        <div style={{
          fontSize: "11px", fontWeight: "700", letterSpacing: "0.09em",
          textTransform: "uppercase", color: appTheme.mutedText,
          fontFamily: appTheme.fontBody, marginBottom: "14px",
        }}>
          What you ate
        </div>
        {renderMeals()}
      </div>

      {/* Delete confirmation sheet */}
      {showDeleteSheet && (
        <>
          <div
            onClick={() => setShowDeleteSheet(false)}
            style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }}
          />
          <div style={{
            position: "fixed", left: 0, right: 0, bottom: 0,
            zIndex: 2101,
            background: appTheme.pageBackground,
            borderRadius: "24px 24px 0 0",
            padding: isMobile ? "24px 20px 44px" : "28px 28px 40px",
            animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          }}>
            <div style={{
              fontFamily: appTheme.fontHeading,
              fontSize: "20px",
              fontWeight: "600",
              color: appTheme.primaryText,
              marginBottom: "6px",
            }}>
              Delete this entry?
            </div>
            <div style={{
              fontFamily: appTheme.fontBody,
              fontSize: "14px",
              color: appTheme.secondaryText,
              marginBottom: "24px",
            }}>
              This cannot be undone.
            </div>
            <div style={{ display: "grid", gap: "10px" }}>
              <button
                onClick={handleDelete}
                style={{
                  width: "100%", padding: "15px", borderRadius: "14px", border: "none",
                  background: "#DC2626", color: "#fff",
                  fontFamily: appTheme.fontBody, fontSize: "15px", fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteSheet(false)}
                style={{
                  width: "100%", padding: "15px", borderRadius: "14px",
                  border: appTheme.cardBorder, background: appTheme.cardBackground,
                  fontFamily: appTheme.fontBody, fontSize: "15px", fontWeight: "600",
                  color: appTheme.primaryText, cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {listView}
      {entryView}
    </>
  );
}

export default JournalPage;
